import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, MessageCircle, Sparkles } from "lucide-react";
import { NAV, waLink } from "../../lib/catalog";
import { MobileMenu } from "./MobileMenu";
import { HeaderCartLink } from "../cart/Cart";
import { CodeSearch } from "./CodeSearch";

const tabCls = (on) =>
  `relative whitespace-nowrap px-1 py-3 text-[13px] font-medium tracking-wide transition-colors duration-300 ${
    on ? "text-[#A0216B]" : "text-[#4a2443] hover:text-[#240620]"
  }`;

const Underline = () => (
  <motion.span layoutId="tab-underline" className="absolute inset-x-0 -bottom-px h-[2px] bg-[#E91E63] rounded-full" />
);

export const Header = () => {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 30);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const active = pathname.startsWith("/c/") ? pathname.slice(3) : null;

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b border-[#240620]/10 bg-[#FEFDEB] transition-shadow duration-500 ${
        scrolled ? "shadow-[0_18px_40px_-28px_rgba(36,6,32,0.6)]" : ""
      }`}
    >
      <div className="mx-auto flex h-[76px] max-w-[1440px] items-center justify-between gap-4 px-4 sm:h-[88px] sm:px-8">
        <Link to="/" data-testid="brand-logo" className="shrink-0 transition-transform duration-500 hover:scale-[1.03]">
          <img src="/brand/logo-tight.webp?v=4" alt="PetalBasket — Flowers at Your Doorstep" className="h-[60px] w-auto sm:h-[76px]" />
        </Link>
        <div className="hidden flex-1 flex-col items-center text-center lg:flex" data-testid="header-tagline">
          <p className="font-display text-2xl font-bold italic leading-none text-[#A0216B] xl:text-[28px]">Celebrate Every Moment with Flowers</p>
          <p className="mt-1.5 font-cinzel text-[10px] font-bold tracking-[0.3em] text-[#4a2443] xl:text-[11px]">Home delivery ◆ Three days prior booking</p>
        </div>
        <div className="flex items-center gap-2">
          <CodeSearch />
          <Link to={{ pathname: "/", hash: "enquire" }} data-testid="header-enquire-btn" className="hidden sm:inline-flex rounded-full border border-[#240620]/30 px-5 py-2.5 text-sm font-semibold text-[#240620] transition-colors duration-300 hover:bg-[#240620] hover:text-[#FFF8EF]">
            Book an enquiry
          </Link>
          <a href={waLink("Hi PetalBasket, I'd like to enquire about flower décor.")} target="_blank" rel="noreferrer" data-testid="header-whatsapp-btn" className="inline-flex h-11 items-center gap-2 rounded-full bg-[#25D366] px-4 text-sm font-semibold text-[#06260f] transition-transform duration-300 hover:scale-105">
            <MessageCircle size={18} /> <span className="hidden sm:inline">WhatsApp</span>
          </a>
          <HeaderCartLink />
          <MobileMenu trigger={<button className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#240620]/25 text-[#240620]" data-testid="mobile-menu-btn" aria-label="Open menu"><Menu size={20} /></button>} />
        </div>
      </div>
      <nav className="hidden md:block border-t border-[#240620]/10" data-testid="category-tabs">
        <div className="no-scrollbar mx-auto flex max-w-[1440px] items-center gap-6 overflow-x-auto px-8 lg:justify-center lg:gap-10">
          {NAV.map((c) => (
            <NavLink key={c.slug} to={`/c/${c.slug}`} className={`${tabCls(active === c.slug)} ${c.festive ? "!text-[#A0216B] font-semibold" : ""}`} data-testid={`tab-${c.slug}`}>
              {c.festive && <Sparkles size={14} className="mr-1 inline -mt-0.5" />}{c.name}
              {active === c.slug && <Underline />}
            </NavLink>
          ))}
        </div>
      </nav>
    </header>
  );
};
